import { Loader } from "@/components/Common/Loader/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { AuthMachineContext } from "@/store/machine/authMachine/authMachine";
import { useNotificationStore } from "@/store/notification";
import { AuthEventType } from "@/types/auth";
import { REQUIRED_MESSAGE } from "@/utils/form";
import { RETRY_LIMIT, RETRY_TIMEOUT, sendCode, verifyCode } from "@/utils/passwordless";
import { Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { sleep } from "@/utils";

type OTPCodeStepProps = {
  email: string;
  hasBackButton?: boolean;
  shouldRefreshOnSuccess?: boolean;
  onCancel: () => void;
  onComplete?: () => void;
};

export const OTPCodeStep = ({ email, hasBackButton, shouldRefreshOnSuccess = false, onCancel }: OTPCodeStepProps) => {
  const displayError = useNotificationStore((state) => state.displayError);
  const authActorRef = AuthMachineContext.useActorRef();
  const [sendCount, setSendCount] = useState(1);
  const [canResend, setCanResend] = useState(true);
  const [isResending, setIsResending] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { toast } = useToast();
  const [canResendInSeconds, setCanResendInSeconds] = useState(0);

  const handleResend = () => {
    if (sendCount > RETRY_LIMIT) {
      displayError({
        title: "ERROR",
        message: "You have requested OTP too many times. Please try again later",
        isPublic: true
      });
      return;
    }

    (async () => {
      try {
        setIsResending(true);

        await sendCode({ email });

        trackEvent(AnalyticsEventType.OTP_RESENT_CODE_COMPLETED);

        toast({ title: "Email sent. Please check your inbox.", variant: "success" });

        setSendCount((prev) => {
          return prev + 1;
        });
        restrictResend();
      } catch (e) {
        trackEvent(AnalyticsEventType.OTP_RESENT_CODE_FAILED);

        displayError({
          title: "ERROR",
          message: (e as Error).message || "Failed to send code",
          isPublic: true
        });
      } finally {
        setIsResending(false);
      }
    })();
  };

  const restrictResend = () => {
    setCanResend(false);
    setCanResendInSeconds(RETRY_TIMEOUT / 1000);

    if (timeoutRef.current) {
      clearInterval(timeoutRef.current);
    }
    timeoutRef.current = setInterval(() => {
      setCanResendInSeconds((prev) => {
        const value = Math.max(0, prev - 1);

        if (value <= 0) {
          if (timeoutRef.current) {
            clearInterval(timeoutRef.current);
          }
          setCanResend(true);
        }

        return value;
      });
    }, 1000);
  };

  useEffect(() => {
    restrictResend();

    return () => {
      clearInterval(timeoutRef.current as unknown as number);
    };
  }, []);

  return (
    <Formik<OTPCodeFormType>
      initialValues={{
        code: ""
      }}
      validationSchema={OTPFormTypeFormSchema}
      onSubmit={async ({ code }) => {
        try {
          const response = await verifyCode({ email, code });

          trackEvent(AnalyticsEventType.OTP_VERIFIED_CODE_COMPLETED);

          restrictResend();

          authActorRef.send({
            type: AuthEventType.SET_TOKEN_AND_LOGIN,
            value: response.id_token
          });

          if (shouldRefreshOnSuccess) {
            await sleep(1000);

            window.location.reload();
            onCancel();

            console.log('reload');
          }
        } catch (e) {
          trackEvent(AnalyticsEventType.OTP_VERIFIED_CODE_FAILED);

          displayError({
            title: "ERROR",
            message: (e as Error).message || "Failed to verify code",
            isPublic: true
          });
        }
      }}
      validateOnMount={true}
    >
      {({
        handleSubmit,
        handleBlur,
        handleChange,
        values,
        isValid,
        isSubmitting,
        touched,
        errors
      }) => {
        return (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>
                Enter OTP sent to your email
                <br />
              </Label>
              <Input
                name="code"
                value={values.code}
                invalid={touched.code && !!errors.code}
                onBlur={handleBlur}
                onChange={handleChange}
                maxLength={6}
              />
              {touched.code && errors.code && (
                <div className="subtext-small capitalize text-red-light-500">{errors.code}</div>
              )}
              <span className="text-sm text-gray-dark-1000">
                Email sent to <span className="text-white">{email}</span>
              </span>
              <a className="cursor-pointer text-sm text-white hover:underline" onClick={onCancel}>
                Send to a different email?
              </a>
            </div>

            <div className="flex flex-row items-center gap-3">
              {hasBackButton && <Button
                type="button"
                variant={"outline"}
                className="gap-3 bg-gray-dark-600 flex-1"
                onClick={onCancel}
              >
                <span>Go back</span>
              </Button>}
              <Button
                type="submit"
                variant={"outline"}
                className="gap-3 bg-gray-dark-600"
                disabled={!isValid || isSubmitting}
              >
                <span>Verify Code</span>
                {isSubmitting && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
              </Button>
            </div>

            {sendCount > 0 && (
              <>
                <Button
                  type="button"
                  variant={"outline"}
                  className="gap-3 bg-gray-dark-600"
                  disabled={isSubmitting || !canResend}
                  onClick={handleResend}
                >
                  <span>Resend OTP</span>
                  {isResending && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
                </Button>
                <div className="flex flex-col justify-center">
                  <span className="text-sm text-gray-dark-1000">
                    Didn't receive code?
                    <br />
                    Request OTP in <span className="text-yellow">{canResendInSeconds}</span> seconds
                  </span>
                </div>
              </>
            )}

            {/* <p className="subtext-small text-gray-dark-1000">
                        <a
                            onClick={onCancel}
                            className="cursor-pointer text-white hover:underline"
                        >
                            Cancel
                        </a>
                    </p> */}
          </form>
        );
      }}
    </Formik>
  );
};

export type OTPCodeFormType = {
  code: string;
};

const OTPFormTypeFormSchema = Yup.object().shape({
  code: Yup.string()
    .min(6, "To have a minimum length of 6 characters")
    .max(6, "To have a maximum length of 6 characters")
    .required(REQUIRED_MESSAGE)
});
